import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import AccountCard from '../components/AccountCard';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { getAdminPlugins, setPluginEnabled } from '../api';
import { pluginText } from '../plugins/pluginFrame';
import { pluginEnabledIn, reloadPlugins } from '../plugins/usePlugins';

/**
 * /admin/plugins: the plugins in plugins/, and where they are switched on.
 *
 * Like the rights, a plugin is switched on somewhere: for all departments
 * (platform-wide plugins.manage) and per department, where a department's own
 * choice overrides the global one and "as global" removes it again
 * (documentation/plugins.md §2). A department administrator sees and changes
 * only their departments.
 */
function AdminPluginsPage() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getAdminPlugins()
      .then((result) => {
        setState(result);
        setError('');
      })
      .catch((err) => setError(err?.message || t('adminPlugins.loadError')));
  }, [t]);

  useEffect(load, [load]);

  async function change(pluginId, departmentId, enabled) {
    setBusy(true);
    try {
      await setPluginEnabled(pluginId, departmentId, enabled);
      reloadPlugins();
      load();
    } catch (err) {
      setError(err?.message || t('adminPlugins.saveError'));
    } finally {
      setBusy(false);
    }
  }

  const statusText = (plugin) => {
    if (plugin.status === 'invalid') return t('adminPlugins.statusInvalid');
    if (plugin.status === 'missing') return t('adminPlugins.statusMissing');
    if (plugin.enabled.platform) {
      return plugin.enabled.off.length
        ? t('adminPlugins.statusAllBut', { count: plugin.enabled.off.length })
        : t('adminPlugins.statusAll');
    }
    return plugin.enabled.on.length
      ? t('adminPlugins.statusSome', { count: plugin.enabled.on.length })
      : t('adminPlugins.statusOff');
  };

  const scope = state?.scope;

  return (
    <PageLayout
      extraSidebarItems={[{ to: '/admin', label: t('adminHome.sidebarTitle'), icon: 'fa-solid fa-user-shield' }]}
    >
      <PageMain>
        <PageHeader
          eyebrow={
            <a href="/admin">
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              {t('adminPanel.backToAdmin')}
            </a>
          }
          title={t('adminPlugins.title')}
          titleId="admin-plugins-title"
          description={t('adminPlugins.description')}
        />
        

        <PageBody className="account-page" aria-labelledby="admin-plugins-title">
          <div className="account-settings-column admin-settings-column">
            <AccountCard
              titleId="admin-plugins-list-title"
              isFirst
              title={t('adminPlugins.installedTitle')}
              description={t('adminPlugins.installedDescription')}
              icon="fa-solid fa-puzzle-piece"
            >
              {error && (
                <div className="login-message login-message-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{error}</span>
                </div>
              )}

              {!state && !error && <p>{t('adminPlugins.loading')}</p>}

              {state && state.plugins.length === 0 && (
                <p className="admin-user-list-empty">{t('adminPlugins.none')}</p>
              )}

              {state?.plugins.map((plugin) => {
                const isOpen = expanded === plugin.id;
                const name = pluginText(plugin.name, i18n.language) || plugin.id;
                const usable = plugin.status === 'installed';

                return (
                  <div className="admin-user-row" key={plugin.id}>
                    <button
                      type="button"
                      className="admin-user-row-header"
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(isOpen ? null : plugin.id)}
                    >
                      <i className={`fa-solid fa-chevron-right admin-user-toggle-icon${isOpen ? ' expanded' : ''}`} />
                      <div className="admin-user-identity">
                        <strong>{name}</strong>
                        <span>
                          {plugin.id}
                          {plugin.version ? ` · ${t('adminPlugins.version', { version: plugin.version })}` : ''}
                        </span>
                      </div>
                      <span className={`admin-user-status${usable && pluginEnabledIn(plugin, null) ? ' active' : ''}`}>
                        {statusText(plugin)}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="admin-user-row-body">
                        {pluginText(plugin.description, i18n.language) && (
                          <p>{pluginText(plugin.description, i18n.language)}</p>
                        )}

                        {plugin.problems.length > 0 && (
                          <div className="admin-user-section">
                            <h4>{t('adminPlugins.problems')}</h4>
                            <ul>
                              {plugin.problems.map((problem) => <li key={problem}>{problem}</li>)}
                            </ul>
                          </div>
                        )}

                        {plugin.status === 'missing' && <p>{t('adminPlugins.missingHint')}</p>}

                        {usable && (
                          <div className="admin-user-section">
                            <h4>{t('adminPlugins.switchedOn')}</h4>

                            {scope?.platform && (
                              <label className="plugin-switch">
                                <input
                                  type="checkbox"
                                  checked={plugin.enabled.platform}
                                  disabled={busy}
                                  onChange={(event) => change(plugin.id, null, event.target.checked)}
                                />
                                {' '}
                                {t('adminPlugins.forAll')}
                              </label>
                            )}

                            <table className="plugin-departments">
                              <tbody>
                                {scope?.departments.map((department) => {
                                  const own = plugin.enabled.on.includes(department.department_id)
                                    ? 'on'
                                    : plugin.enabled.off.includes(department.department_id)
                                      ? 'off'
                                      : 'inherit';
                                  return (
                                    <tr key={department.department_id}>
                                      <th scope="row">{department.name}</th>
                                      <td>
                                        <select
                                          value={own}
                                          disabled={busy}
                                          aria-label={t('adminPlugins.departmentSwitch', { department: department.name })}
                                          onChange={(event) => change(
                                            plugin.id,
                                            department.department_id,
                                            event.target.value === 'inherit' ? null : event.target.value === 'on',
                                          )}
                                        >
                                          <option value="inherit">
                                            {t(plugin.enabled.platform ? 'adminPlugins.inheritOn' : 'adminPlugins.inheritOff')}
                                          </option>
                                          <option value="on">{t('adminPlugins.on')}</option>
                                          <option value="off">{t('adminPlugins.off')}</option>
                                        </select>
                                      </td>
                                      <td>
                                        {plugin.hasSettings && (
                                          <Button
                                            to={`/admin/plugins/${plugin.id}?department=${department.department_id}`}
                                            className="plugin-settings-button"
                                          >
                                            {t('adminPlugins.settings')}
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>

                            {plugin.hasSettings && scope?.platform && (
                              <p className="plugin-global-settings">
                                <Button to={`/admin/plugins/${plugin.id}`}>{t('adminPlugins.globalSettings')}</Button>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </AccountCard>
          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default AdminPluginsPage;
